import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, MapPin, ShieldCheck, ArrowRight, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { PageHero, Section, Button, PROVINCES, SERVICES } from "@/components/UI";

export default function Registry() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [province, setProvince] = useState("All");
  const [service, setService] = useState("All");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/practitioners", { params: { q: q || undefined, province, service } });
      setItems(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [province, service]);
  useEffect(() => {
    const t = setTimeout(load, 350);
    return () => clearTimeout(t);
    /* eslint-disable-next-line */
  }, [q]);

  return (
    <>
      <PageHero overline="Practitioner Registry" testId="registry-hero"
        title="A national registry, in development"
        subtitle="As CCPM establishes Canada's national standard, certified practitioners who have been verified against our standards of practice will be listed here. The registry is being built — please check back as it grows." />

      <Section className="py-16 lg:py-24">
        <div className="grid lg:grid-cols-4 gap-10">
          <aside className="lg:col-span-1">
            <div className="lg:sticky lg:top-28 space-y-8">
              <div>
                <label className="overline text-ccpm-taupe block mb-3">Search</label>
                <div className="relative">
                  <Search size={17} strokeWidth={1.5} className="absolute left-3 top-1/2 -translate-y-1/2 text-ccpm-taupe" />
                  <input data-testid="registry-search-input" value={q} onChange={(e) => setQ(e.target.value)}
                    placeholder="Name, clinic or city" className="w-full pl-10 pr-3 py-3 bg-white border border-ccpm-border text-sm focus:outline-none focus:border-ccpm-olive" />
                </div>
              </div>
              <div>
                <label className="overline text-ccpm-taupe block mb-3">Province</label>
                <select data-testid="registry-province-filter" value={province} onChange={(e) => setProvince(e.target.value)}
                  className="w-full px-3 py-3 bg-white border border-ccpm-border text-sm focus:outline-none focus:border-ccpm-olive">
                  <option value="All">All provinces</option>
                  {PROVINCES.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label className="overline text-ccpm-taupe block mb-3">Service</label>
                <select data-testid="registry-service-filter" value={service} onChange={(e) => setService(e.target.value)}
                  className="w-full px-3 py-3 bg-white border border-ccpm-border text-sm focus:outline-none focus:border-ccpm-olive">
                  <option value="All">All services</option>
                  {SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="pt-6 border-t border-ccpm-border">
                <p className="text-sm text-ccpm-taupe font-light leading-relaxed">Are you a practitioner interested in future CCPM certification? Register your interest and we'll keep you informed.</p>
                <Button to="/registry/apply" variant="outline" className="mt-4 !px-5 !py-2.5 !text-[13px] w-full" data-testid="registry-apply-btn">Register your interest</Button>
              </div>
            </div>
          </aside>

          <div className="lg:col-span-3">
            <div className="flex items-center justify-between mb-6 pb-4 border-b border-ccpm-border">
              <p className="text-sm text-ccpm-taupe" data-testid="registry-count">{loading ? "Searching…" : items.length === 0 ? "Registry in development" : `${items.length} verified ${items.length === 1 ? "practitioner" : "practitioners"}`}</p>
              <span className="inline-flex items-center gap-1.5 text-xs text-ccpm-olive"><ShieldCheck size={15} strokeWidth={1.5} /> CCPM verified</span>
            </div>

            {loading ? (
              <div className="flex justify-center py-24 text-ccpm-olive"><Loader2 className="animate-spin" /></div>
            ) : items.length === 0 ? (
              <div className="border border-ccpm-border bg-white px-8 py-16 lg:py-24 text-center" data-testid="registry-empty">
                <div className="mx-auto w-14 h-14 flex items-center justify-center rounded-full bg-ccpm-stone mb-6">
                  <ShieldCheck size={26} strokeWidth={1.4} className="text-ccpm-olive" />
                </div>
                <h3 className="font-serif text-3xl lg:text-4xl text-ccpm-charcoal">The national registry is being established</h3>
                <p className="mt-4 max-w-md mx-auto text-ccpm-taupe font-light leading-relaxed">
                  CCPM is a new organization developing Canada's national standard for restorative paramedical micropigmentation. Certification is not yet available. As it launches and practitioners are verified, they will be listed here so the public can find them with confidence.
                </p>
                <div className="mt-8 flex flex-wrap gap-4 justify-center">
                  <Button to="/registry/apply" variant="primary" data-testid="empty-apply-btn">Register your interest</Button>
                  <Button to="/contact" variant="outline" data-testid="empty-notify-btn">Ask to be notified</Button>
                </div>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-5" data-testid="registry-results">
                {items.map((p, i) => (
                  <motion.div key={p.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: (i % 4) * 0.05 }}>
                    <Link to={`/registry/${p.id}`}
                      className="group block h-full bg-white border border-ccpm-border p-7 transition-all duration-300 hover:-translate-y-1 hover:border-ccpm-olive hover:shadow-[0_8px_30px_rgba(107,112,92,0.08)]"
                      data-testid={`practitioner-card-${p.id}`}>
                      <div className="flex items-start gap-4">
                        <div className="w-16 h-16 shrink-0 overflow-hidden bg-ccpm-stone rounded-sm">
                          {p.photo_url
                            ? <img src={p.photo_url} alt={p.name} className="w-full h-full object-cover" />
                            : <div className="w-full h-full flex items-center justify-center text-ccpm-olive-soft font-serif text-2xl">{p.name.charAt(0)}</div>}
                        </div>
                        <div className="min-w-0">
                          <h3 className="font-serif text-2xl text-ccpm-charcoal leading-tight group-hover:text-ccpm-olive transition-colors">{p.name}</h3>
                          <p className="text-xs text-ccpm-olive font-semibold mt-1">{p.credentials}</p>
                          <p className="mt-1 inline-flex items-center gap-1.5 text-sm text-ccpm-taupe"><MapPin size={14} strokeWidth={1.5} />{p.city}, {p.province}</p>
                        </div>
                        <ShieldCheck size={20} strokeWidth={1.5} className="text-ccpm-olive shrink-0 ml-auto" />
                      </div>
                      {p.clinic_name && <p className="mt-4 text-sm text-ccpm-charcoal">{p.clinic_name}</p>}
                      <div className="mt-3 flex flex-wrap gap-2">
                        {p.services.map((s) => <span key={s} className="text-[11px] px-2.5 py-1 bg-ccpm-stone text-ccpm-taupe border border-ccpm-border">{s}</span>)}
                      </div>
                      {p.bio && <p className="mt-4 text-sm text-ccpm-taupe font-light leading-relaxed line-clamp-2">{p.bio}</p>}
                      <span className="mt-5 inline-flex items-center gap-1.5 text-xs font-semibold text-ccpm-olive">View profile <ArrowRight size={13} className="group-hover:translate-x-1 transition-transform" /></span>
                    </Link>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>
      </Section>
    </>
  );
}
